/**
 * Doctor audio capture for AI Clinical Copilot (integration spec).
 *
 * Records the doctor's audio from the SAME Zego stream being published — not
 * a second getUserMedia session — and uploads the single audio file to
 * POST /api/bookings/:bookingId/ai-session/transcript once the call ends.
 *
 * Key requirements from the integration spec:
 *   - No second microphone session (reuse the published MediaStream)
 *   - Start capture only after publishing succeeds
 *   - Stop before/during call teardown
 *   - One upload per call, multipart/form-data, field name "audio"
 *   - No speaker field (backend derives from auth)
 *   - 10MB limit per upload
 *   - Never let capture/upload failure affect the call itself
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { log } from "@/lib/logger";
import { videoCallService } from "@/services/video-call/video-call.service";

const MIME_TYPE = "audio/webm;codecs=opus";
const MAX_BLOB_SIZE = 10 * 1024 * 1024; // 10MB (matches backend multer limit)

export interface AudioCaptureController {
  /** True while actively recording. */
  recording: boolean;
  /** True when an upload is in progress. */
  uploading: boolean;
  /** Non-fatal error for UI display. */
  notice: string | null;
  /** Starts recording (called when call goes live). */
  start: () => void;
  /** Stops recording and uploads (called when call ends). */
  stopAndUpload: (bookingId: string) => Promise<void>;
  /** Dismisses the current notice. */
  clearNotice: () => void;
}

export function useAudioCapture(
  localStream: MediaStream | null,
  callLive: boolean,
): AudioCaptureController {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(localStream);
  const callLiveRef = useRef(callLive);
  /**
   * Set once `stopAndUpload` has run for this call.
   *
   * `callLive` stays true through the `ending` state (on purpose — see
   * `bookings.call.tsx`), but `stopAndUpload` is deliberately called during
   * `ending`, before the published stream's tracks are stopped, so the final
   * chunk is captured while the stream is still live. Without this flag the
   * auto-start effect below would see `recording` flip back to false and
   * immediately open a new recorder on a stream about to be torn down.
   */
  const finalizedRef = useRef(false);

  streamRef.current = localStream;
  callLiveRef.current = callLive;

  const clearNotice = useCallback(() => setNotice(null), []);

  const start = useCallback(() => {
    if (recording || finalizedRef.current) return;
    const stream = streamRef.current;
    if (!stream) {
      setNotice("No audio stream available for recording.");
      return;
    }
    if (!callLiveRef.current) {
      setNotice("Call not live; recording not started.");
      return;
    }

    try {
      const recorder = new MediaRecorder(stream, { mimeType: MIME_TYPE });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onerror = (event) => {
        // Not in TS's DOM lib — the spec puts a DOMException on `.error`.
        const error = (event as Event & { error?: DOMException }).error;
        log.warn("copilot", "audioCapture.recorderError", {
          error: error?.message ?? String(error),
        });
        setNotice("Audio recording encountered an error.");
      };

      recorder.start(1000); // Collect data every second
      setRecording(true);
      setNotice(null);
      log.info("copilot", "audioCapture.started", { mimeType: MIME_TYPE });
    } catch (error) {
      log.warn("copilot", "audioCapture.startFailed", {
        error: error instanceof Error ? error.message : String(error),
      });
      setNotice("Could not start audio recording.");
    }
  }, [recording]);

  const stopAndUpload = useCallback(async (bookingId: string) => {
    finalizedRef.current = true;
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      log.debug("copilot", "audioCapture.stopNoOp", { state: recorder?.state });
      return;
    }

    // Stop recording and wait for final dataavailable
    await new Promise<void>((resolve) => {
      const finalize = () => {
        recorder.removeEventListener("dataavailable", onData);
        recorder.removeEventListener("stop", finalize);
        resolve();
      };
      const onData = () => {
        // Final chunk will arrive before 'stop'
      };
      recorder.addEventListener("dataavailable", onData);
      recorder.addEventListener("stop", finalize);
      recorder.stop();
    });

    setRecording(false);
    mediaRecorderRef.current = null;

    const chunks = chunksRef.current;
    chunksRef.current = [];

    if (chunks.length === 0) {
      log.debug("copilot", "audioCapture.noData", {});
      return;
    }

    const blob = new Blob(chunks, { type: MIME_TYPE });
    log.info("copilot", "audioCapture.stopped", {
      size: blob.size,
      durationChunks: chunks.length,
    });

    // Check size limit (backend enforces 10MB via multer)
    if (blob.size > MAX_BLOB_SIZE) {
      const msg = `Recording exceeds 10MB limit (${Math.round(blob.size / 1024 / 1024)}MB).`;
      log.warn("copilot", "audioCapture.sizeLimit", { size: blob.size });
      setNotice(msg);
      return;
    }

    // Upload — wrapped so a failure never affects the call
    setUploading(true);
    try {
      await videoCallService.uploadDoctorTranscript(bookingId, blob);
      log.info("copilot", "audioCapture.uploaded", { bookingId });
      setNotice(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed.";
      log.warn("copilot", "audioCapture.uploadFailed", { error: message });
      // Per spec: never let upload failure affect the call — just log and show notice
      setNotice(`Audio upload failed: ${message}. The consultation is unaffected.`);
    } finally {
      setUploading(false);
    }
  }, []);

  // Auto-start when call goes live and stream is available
  useEffect(() => {
    if (callLive && localStream && !recording) {
      start();
    }
  }, [callLive, localStream, recording, start]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          /* already stopping */
        }
      }
      mediaRecorderRef.current = null;
      chunksRef.current = [];
    },
    [],
  );

  return { recording, uploading, notice, start, stopAndUpload, clearNotice };
}
